import * as React from 'react';
import Grid from "@mui/material/Grid";
import TransactionIndex from "./TransactionIndex";
import {useState} from "react";
import {useSearchParams} from "react-router-dom";
import {Stack, Dialog, DialogTitle, DialogContent, DialogActions, InputLabel, Select, MenuItem, TextField} from "@mui/material";
import {Refresh, FileDownload} from "@mui/icons-material";
import Button from "@mui/material/Button";
import TransactionFilter from "./TransactionFilter";
import IconButton from "@mui/material/IconButton";
import helpers from '../../helpers/common';
import PageHeader from '../layout/PageHeader';
import SectionCard from '../layout/SectionCard';
import FilterPanel from '../layout/FilterPanel';
import transactions from "../../models/transactions";
import {LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterMoment} from "@mui/x-date-pickers/AdapterMoment";
import {DateTimePicker} from "@mui/x-date-pickers/DateTimePicker";
import moment from "moment";

const DATE_FORMAT = 'DD/MM/YYYY HH:mm';

export default function TransactionContainer() {
    const [searchParams, setSearchParams] = useSearchParams()
    const [refreshId, setRefreshId] = useState(0)
    const [exportDialogOpen, setExportDialogOpen] = useState(false)
    const [exportFilters, setExportFilters] = useState({
        statuses: [],
        created_from: null,
        created_to: null,
    })
    const accordionExpanded = Array.from(searchParams).length > 0

    const refresh = () => {
        setRefreshId((prevId) => {
            return prevId + 1
        })
    }

    const openExportDialog = () => {
        const selectedStatuses = searchParams.getAll('status')
        const createdFrom = searchParams.get('created_from')
        const createdTo = searchParams.get('created_to')
        setExportFilters({
            statuses: selectedStatuses,
            created_from: createdFrom ? moment.utc(createdFrom) : null,
            created_to: createdTo ? moment.utc(createdTo) : null,
        })
        setExportDialogOpen(true)
    }

    const getExportUrl = () => {
        const params = new URLSearchParams()
        // Keep all current filters except export-controlled ones
        searchParams.forEach((value, key) => {
            if (key === 'status' || key === 'created_from' || key === 'created_to' || key === 'export') return
            params.append(key, value)
        })

        exportFilters.statuses.forEach((status) => params.append('status', status))
        if (exportFilters.created_from) params.set('created_from', exportFilters.created_from.toISOString())
        if (exportFilters.created_to) params.set('created_to', exportFilters.created_to.toISOString())
        if (helpers.forcedMerchantID) params.set('merchant_id', helpers.forcedMerchantID)
        params.set('export', '')

        return `/iapi/transactions?${params.toString()}`
    }

    return <React.Fragment>
        <Grid size={12}>
            <PageHeader
                title="Transactions"
                breadcrumbs={[['Dashboard', '/'], ['Transactions', '/transactions']]}
                actions={
                    <>
                        <Button onClick={refresh} variant="contained" startIcon={<Refresh />}>
                            Refresh
                        </Button>
                        <Button
                            onClick={openExportDialog}
                            variant="outlined"
                            startIcon={<FileDownload />}
                        >
                            Export CSV
                        </Button>
                    </>
                }
            />
        </Grid>
        <Grid size={12}>
            <FilterPanel
                defaultExpanded={accordionExpanded}
                title="Filters"
                sx={{ '& .MuiAccordionSummary-root': { py: 0.5 } }}
            >
                <TransactionFilter />
            </FilterPanel>
        </Grid>
        <Grid size={12}>
            <Stack spacing={1.5} direction="row" sx={{display: {xs: 'inherit', md: 'none'}}}>
                <IconButton onClick={refresh} color="primary"><Refresh /></IconButton>
                <IconButton
                    onClick={openExportDialog}
                    color="primary"
                >
                    <FileDownload />
                </IconButton>
            </Stack>
        </Grid>
        <Grid size={12}>
            <SectionCard sx={{ display: 'flex', flexDirection: 'column', overflowX: 'auto'}}>
                <TransactionIndex fullPage={true} searchParams={searchParams} setSearchParams={setSearchParams} refreshId={refreshId} refresh={refresh}/>
            </SectionCard>
        </Grid>
        <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Export CSV</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <div>
                        <InputLabel>Statuses</InputLabel>
                        <Select
                            multiple
                            fullWidth
                            value={exportFilters.statuses}
                            onChange={(ev) => {
                                const value = ev.target.value
                                setExportFilters((prev) => ({
                                    ...prev,
                                    statuses: Array.isArray(value) ? value : [value]
                                }))
                            }}
                            renderValue={(selected) => {
                                if (!selected || selected.length === 0) return 'All statuses'
                                return selected
                                    .map((s) => transactions.enumToStatus[s] || s)
                                    .map((s) => s.capitalize())
                                    .join(', ')
                            }}
                        >
                            {Object.keys(transactions.statusToEnum).map((statusKey) => (
                                <MenuItem key={`export-status-${statusKey}`} value={transactions.statusToEnum[statusKey]}>
                                    {statusKey.capitalize()}
                                </MenuItem>
                            ))}
                        </Select>
                    </div>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DateTimePicker
                            label="Created from"
                            ampm={false}
                            inputFormat={DATE_FORMAT}
                            value={exportFilters.created_from}
                            onChange={(newValue) => {
                                setExportFilters((prev) => ({ ...prev, created_from: newValue || null }))
                            }}
                            renderInput={(params) => <TextField fullWidth size="small" {...params} />}
                        />
                    </LocalizationProvider>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DateTimePicker
                            label="Created to"
                            ampm={false}
                            inputFormat={DATE_FORMAT}
                            value={exportFilters.created_to}
                            onChange={(newValue) => {
                                setExportFilters((prev) => ({ ...prev, created_to: newValue || null }))
                            }}
                            renderInput={(params) => <TextField fullWidth size="small" {...params} />}
                        />
                    </LocalizationProvider>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setExportDialogOpen(false)}>Cancel</Button>
                <Button
                    component="a"
                    href={getExportUrl()}
                    onClick={() => setExportDialogOpen(false)}
                    variant="contained"
                    startIcon={<FileDownload />}
                >
                    Download CSV
                </Button>
            </DialogActions>
        </Dialog>
    </React.Fragment>
}